/**
 * examDates.ts
 * ────────────
 * Rendering for ExamConfig.exam_date, the day an admin typed off the
 * official edital.
 *
 * The wire format is a plain "YYYY-MM-DD" with no time and no zone, because
 * an exam is a day rather than an instant. Everything here treats it that
 * way, which is the point: `new Date('2026-10-05')` parses as UTC midnight,
 * so a student anywhere west of Greenwich would read it back as 4 October
 * and see a countdown one day long. Maputo is UTC+2, so that bug would never
 * reproduce on the founder's phone or on the server, only on a traveller's.
 * parseExamDay builds the date from its parts in local time instead.
 *
 * Nothing here decides whether to render. A null date, an unparseable one and
 * a past one all return null, and the caller draws nothing at all. See
 * ExamConfig.exam_date in schema.prisma for why those three cases are
 * deliberately indistinguishable to the student.
 */

const MONTHS_PT = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
] as const

/** "2026-10-05" → local midnight on 5 October 2026. Null if malformed. */
function parseExamDay(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/** Whole days from today to the exam. 0 = today. Negative = already sat. */
export function daysUntilExam(iso: string | null | undefined): number | null {
  const day = parseExamDay(iso)
  if (!day) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  // Rounded, not floored: a DST boundary between now and the exam makes one
  // of these "days" 23 or 25 hours long, and flooring would drop or add one.
  return Math.round((day.getTime() - today.getTime()) / 86_400_000)
}

/** "2026-10-05" → "5 Out". Null if malformed. */
export function formatExamDay(iso: string | null | undefined): string | null {
  const day = parseExamDay(iso)
  if (!day) return null
  return `${day.getDate()} ${MONTHS_PT[day.getMonth()]}`
}

/**
 * The full line for a subject card: "5 Out · faltam 49 dias".
 *
 * Null whenever there is nothing honest to show, including for a date that
 * has passed — the row stays in the database so an admin can see it needs
 * replacing, but the student is told nothing rather than shown a negative
 * number.
 */
export function examCountdownLabel(iso: string | null | undefined): string | null {
  const days = daysUntilExam(iso)
  const day  = formatExamDay(iso)
  if (days === null || day === null || days < 0) return null
  if (days === 0) return `${day} · o exame é hoje`
  if (days === 1) return `${day} · falta 1 dia para o exame`
  return `${day} · faltam ${days} dias para o exame`
}

/** Just the countdown half, for the strip: "Faltam 49 dias". */
export function examCountdownSentence(iso: string | null | undefined): string | null {
  const days = daysUntilExam(iso)
  if (days === null || days < 0) return null
  if (days === 0) return 'É hoje'
  if (days === 1) return 'Falta 1 dia'
  return `Faltam ${days} dias`
}
